<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { api, NetworkError } from '@/lib/http.js'
import { listRecords } from '@/lib/records.js'
import { ENTITIES } from '@/lib/entities.js'
import { entityLabel } from '@/lib/labels.js'
import { useDebounce } from '@/composables/useDebounce.js'
import { useUi } from '@/stores/ui.js'
import { useSession } from '@/stores/session.js'
import { prefillFromText } from '@/lib/quickCreate.js'

/**
 * The search box of the top bar: fragments across every entity (D-11); Enter opens the full page.
 * On a phone the box is narrow, so its results open across the screen under the bar, not as
 * narrow as the box.
 */
const router = useRouter()
const ui = useUi()
const session = useSession()
const q = ref('')
// Not found? Create it from here, with what was typed as its name.
const creatable = computed(() => ['account', 'contact', 'lead', 'opportunity', 'competitor', 'campaign', 'event'].filter((e) => session.has(`${e}:create`)))
function create(entity) {
  const typed = q.value.trim()
  open.value = false
  q.value = ''
  ui.openCreate({ entity, defaults: prefillFromText(entity, typed) })
}
const open = ref(false)
const loading = ref(false)
const groups = ref([])
const root = ref(null)
const input = ref(null)
// Where the results start on a phone (under the box, across the screen); null on wider screens.
const phoneTop = ref(null)
function place() {
  const box = root.value?.getBoundingClientRect()
  phoneTop.value = box && window.innerWidth < 640 ? Math.round(box.bottom + 8) : null
}
watch(open, (now) => now && place())

const search = useDebounce(async () => {
  if (q.value.trim().length < 2) {
    groups.value = []
    return
  }
  loading.value = true
  try {
    groups.value = (await api('/search', { query: { q: q.value, limit: 4 }, timeoutMs: 8000 })).data
  } catch (error) {
    if (!(error instanceof NetworkError)) throw error
    const out = []
    for (const entity of ['account', 'contact', 'lead', 'opportunity', 'offer']) {
      const res = await listRecords(entity, { filter: { q: q.value }, page: { size: 4 } })
      if (res.data.length) out.push({ entity, total: res.meta.total, items: res.data })
    }
    groups.value = out
  } finally {
    loading.value = false
  }
}, 250)

function go(entity, uuid) {
  open.value = false
  q.value = ''
  router.push({ name: 'record', params: { entityPath: ENTITIES[entity].path, uuid } })
}
function submit() {
  if (q.value.trim().length < 2) return
  open.value = false
  router.push({ name: 'search', query: { q: q.value.trim() } })
}
function outside(e) {
  if (open.value && root.value && !root.value.contains(e.target)) open.value = false
}
function shortcut(e) {
  if ((e.key === 'k' && (e.ctrlKey || e.metaKey)) || (e.key === '/' && !['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName) && !document.activeElement?.isContentEditable)) {
    e.preventDefault()
    input.value?.focus()
  }
}
onMounted(() => {
  document.addEventListener('mousedown', outside)
  document.addEventListener('keydown', shortcut)
  window.addEventListener('resize', place)
})
onBeforeUnmount(() => {
  document.removeEventListener('mousedown', outside)
  document.removeEventListener('keydown', shortcut)
  window.removeEventListener('resize', place)
})
</script>

<template>
  <div ref="root" class="relative w-full max-w-xl">
    <form role="search" @submit.prevent="submit">
      <i class="fa-solid fa-magnifying-glass pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-faint" aria-hidden="true"></i>
      <input
        ref="input"
        v-model="q"
        type="search"
        class="input rounded-full pl-8"
        :placeholder="$t('search.placeholder')"
        :aria-label="$t('common.search')"
        data-testid="global-search"
        @focus="open = true"
        @input="open = true; search()"
        @keydown.escape="open = false"
      />
    </form>
    <div
      v-if="open && q.trim().length >= 2"
      :class="['surface z-40 overflow-y-auto rounded-xl border border-default py-2 shadow-2xl', phoneTop === null ? 'absolute mt-2 max-h-[70vh] w-full' : 'fixed inset-x-2']"
      :style="phoneTop === null ? null : { top: `${phoneTop}px`, maxHeight: `calc(100dvh - ${phoneTop + 16}px)` }"
      data-testid="search-results"
    >
      <p v-if="loading && !groups.length" class="px-4 py-2 text-sm text-faint"><i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i> {{ $t('common.loading') }}</p>
      <p v-else-if="!groups.length" class="px-4 py-2 text-sm text-faint">{{ $t('common.nothingFound') }}</p>
      <div v-for="g in groups" :key="g.entity" class="py-1">
        <p class="px-4 pb-1 text-2xs font-semibold uppercase tracking-wide text-faint">{{ entityLabel(g.entity, 'many') }} · {{ g.total }}</p>
        <button v-for="item in g.items" :key="item.uuid" type="button" class="flex w-full items-center gap-2 px-4 py-1.5 text-left text-sm hover:bg-[var(--surface-3)]" @click="go(g.entity, item.uuid)">
          <i :class="['fa-solid fa-fw text-xs', ENTITIES[g.entity].icon, ENTITIES[g.entity].color]" aria-hidden="true"></i>
          <span v-private class="min-w-0 flex-1 truncate">{{ item.label }}</span>
          <span class="text-xs text-faint">{{ item.number }}</span>
        </button>
      </div>
      <button type="button" class="mt-1 w-full border-t border-default px-4 pt-2 text-left text-sm link" @click="submit">{{ $t('search.resultsFor', { q }) }} →</button>
      <div v-if="creatable.length" class="mt-2 flex flex-wrap items-center gap-1.5 border-t border-default px-4 pt-2" data-testid="search-create">
        <span class="text-xs text-faint">{{ $t('create.fromSearch', { q: q.trim() }) }}</span>
        <button v-for="e in creatable" :key="e" type="button" class="inline-flex items-center gap-1 rounded-full border border-default px-2.5 py-1 text-xs hover:bg-[var(--surface-3)]" :data-testid="`search-create-${e}`" @mousedown.prevent="create(e)">
          <i :class="['fa-solid fa-plus text-3xs', ENTITIES[e].color]" aria-hidden="true"></i>{{ entityLabel(e) }}
        </button>
      </div>
    </div>
  </div>
</template>
